import React, { createContext, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import * as yup from "yup";

import { Item } from "../models/item";
import { ItemStatus } from "../models/item.status";
import { ItemTypes } from "../models/item.types";
import { JobMode } from "../models/job.mode";
import { Room } from "../models/room";
import { ItemJob } from "../models/sales";
import { Symbol } from "../models/symbol";
import ItemJobService from "../services/item.job.service";
import ItemTypesService from "../services/item.types.service";
import ItemsService from "../services/items.service";
import ModeJobService from "../services/mode.job.service";
import OrderService from "../services/order.service";
import RoomService from "../services/room.service";
import { AvailableIn, Status, SymbolType } from "../util/enums";
import { useAppState } from "./StateContext";

interface ActualInventoryProps {
  itemsJob?: ItemJob[];
  rooms?: Room[];
  itemsType: ItemTypes[];
  itemStatus?: ItemStatus[];
  items?: Item[];
  isLoadingItemList: boolean;
  itemsHardwareBox?: Item[];
  exceptionSymbols?: Symbol[];
  locationSymbols?: Symbol[];
  itemJob?: ItemJob;
  setItemJob: React.Dispatch<React.SetStateAction<ItemJob | undefined>>;
  uuidItemJob?: string;
  setUUIDItemJob: React.Dispatch<React.SetStateAction<string | undefined>>;
  jobModes?: JobMode[];
  descriptiveSymbols?: Symbol[];
  currentStep: string;
  nextStep: () => void;
  previousStep: () => void;
  stepsOption: string[];
  errors?: yup.ValidationError[];
  setErrors: React.Dispatch<React.SetStateAction<yup.ValidationError[]>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<string>>;
  setStepsOption: React.Dispatch<React.SetStateAction<string[]>>;
}

export enum Step {
  DETAILS = "details",
  CHECKLIST = "checklist",
  PHOTOS = "photos",
  CONDITIONS = "conditions",
  UPLOAD_INVENTORY_PDF = "upload",
}

const ActualInventoryContext = createContext<ActualInventoryProps>({} as ActualInventoryProps);

interface ActualInventoryProviderProps {
  children: React.ReactChild;
  fromBranchId?: number;
  toBranchId?: number;
}

const ActualInventoryProvider = ({
  children,
  fromBranchId,
  toBranchId,
}: ActualInventoryProviderProps) => {
  const { currentUser } = useAppState();

  const [rooms, setRooms] = useState<Room[]>([]);
  const [itemsType, setItemsType] = useState<ItemTypes[]>([]);
  const [itemStatus, setItemStatus] = useState<ItemStatus[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [itemsHardwareBox, setItemsHardwareBox] = useState<Item[]>([]);
  const [exceptionSymbols, setExceptionSymbols] = useState<Symbol[]>([]);
  const [locationSymbols, setLocationSymbols] = useState<Symbol[]>([]);
  const [descriptiveSymbols, setDescriptiveSymbols] = useState<Symbol[]>([]);
  const [itemJob, setItemJob] = useState<ItemJob | undefined>();
  const [jobModes, setJobModes] = useState<JobMode[]>();
  const [uuidItemJob, setUUIDItemJob] = useState<string>();
  const [stepsOption, setStepsOption] = useState<string[]>([Step.DETAILS]);

  const [currentStep, setCurrentStep] = useState<string>(Step.DETAILS);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  useQuery(
    ["job.mode.get.all"],
    () =>
      ModeJobService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setJobModes(response.data);
        }
      },
    }
  );

  useQuery(
    ["item.job.get.item.status"],
    () =>
      ItemJobService.getItemStatus({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response.data && response.data.length > 0) setItemStatus(response.data);
      },
    }
  );

  useQuery(
    ["room.get.all"],
    () =>
      RoomService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        jobId: "",
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response.data && response.data.length > 0) setRooms(response.data);
      },
    }
  );

  const orderBranchesId = [fromBranchId, toBranchId].filter(
    (id): id is number => typeof id === "number"
  );

  useQuery(
    ["item.type.get.all"],
    () =>
      ItemTypesService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
        branchesId: orderBranchesId,
        include: JSON.stringify([{ association: "rooms", attributes: ["id", "name", "code"] }]),
      }),
    {
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setItemsType(response.data);
      },
    }
  );

  const { isFetching: isLoadingItemList } = useQuery(
    ["items.ops.getAll"],
    () =>
      ItemsService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        availableIn: [AvailableIn.BOTH, AvailableIn.OPS],
      }),
    {
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setItems(response.data);
          setItemsHardwareBox(response.data.filter((i) => i.isHardwareBox));
        }
      },
    }
  );

  useQuery(
    ["order.get.symbols"],
    () =>
      OrderService.getSymbols({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          const symbols = response.data as Symbol[];

          setExceptionSymbols(symbols?.filter((symbol) => symbol.type === SymbolType.EXCEPTION));
          setLocationSymbols(symbols?.filter((symbol) => symbol.type === SymbolType.LOCATION));
          setDescriptiveSymbols(
            symbols?.filter((symbol) => symbol.type === SymbolType.DESCRIPTIVE)
          );
        }
      },
    }
  );

  const nextStep = () => {
    switch (currentStep) {
      case Step.DETAILS:
        if (itemJob?.item?.mandatoryME || !!itemJob?.isMeCustom) setCurrentStep(Step.CHECKLIST);
        else setCurrentStep(Step.PHOTOS);
        break;
      case Step.CHECKLIST:
        setCurrentStep(Step.PHOTOS);
        break;
      case Step.PHOTOS:
        setCurrentStep(Step.CONDITIONS);
        break;
      default:
        break;
    }

    const stepsOptionUpdated = [...stepsOption];

    stepsOptionUpdated.push(currentStep);
    setStepsOption(stepsOptionUpdated);
  };

  const previousStep = () => {
    switch (currentStep) {
      case Step.PHOTOS:
        if (itemJob?.item?.mandatoryME || !!itemJob?.isMeCustom) setCurrentStep(Step.CHECKLIST);
        else setCurrentStep(Step.DETAILS);
        break;
      case Step.CHECKLIST:
        setCurrentStep(Step.DETAILS);
        break;
      case Step.CONDITIONS:
        setCurrentStep(Step.PHOTOS);
        break;
      default:
        break;
    }

    const stepsOptionUpdated = [...stepsOption];

    stepsOptionUpdated.pop();
    setStepsOption(stepsOptionUpdated);
  };

  return (
    <ActualInventoryContext.Provider
      value={{
        setCurrentStep,
        setStepsOption,
        errors,
        setErrors,
        stepsOption,
        currentStep,
        nextStep,
        previousStep,
        jobModes,
        uuidItemJob,
        descriptiveSymbols,
        setUUIDItemJob,
        itemJob,
        setItemJob,
        rooms,
        itemsType,
        itemStatus,
        items,
        isLoadingItemList,
        itemsHardwareBox,
        exceptionSymbols,
        locationSymbols,
      }}
    >
      {children}
    </ActualInventoryContext.Provider>
  );
};

export { ActualInventoryContext, ActualInventoryProvider };
